import { DEBT_MODULE_TASK_IDS, QUOTATIONS_ENABLED, type DebtModuleAccess } from './debtModules';
import { DEFAULT_DEBTS_NAV_BETA, NAV_BETA_PARAM, navBetaFromUrl, type DebtsNavBeta } from './navBeta';
import { SCOPE_PARAM, readScopeParam } from '../portfolio/scopeParam';

export interface DebtsNavItem {
  key: string;
  path: string;
  labelKey: string;
  moduleTaskId?: number;
  isPreview?: boolean;
  isPendingRoute?: boolean;
  isHidden?: boolean;
  aliases?: readonly string[];
  children?: readonly DebtsNavItem[];
}

export type DebtsNavGroupKey =
  | 'portfolio' | 'operations' | 'closedMonth' | 'structuring' | 'reference'
  | 'owe' | 'costs' | 'maturing' | 'closed';

export interface DebtsNavGroup {
  key: DebtsNavGroupKey;
  labelKey: string;
  items: readonly DebtsNavItem[];
}

export const DEBTS_OVERVIEW_PATH = '/debts';

export const DEBTS_COVENANTS_PATH = '/debts/covenants';

export const DEBTS_FAIR_VALUE_PATH = '/debts/fair-value';

export const DEBTS_COMPANIES_PATH = '/debts/companies';

export const DEBTS_MOVEMENTS_PATH = '/debts/movements';

export const DEBTS_MATURITIES_PATH = '/debts/maturities';

export const DEBTS_MARKET_INDEXES_PATH = '/debts/market-indexes';

export const DEBTS_COST_INDEXER_PATH = '/debts/cost/indexer';

export const DEBTS_COST_FUNDING_PATH = '/debts/cost/funding';

export const DEBTS_COST_GUARANTEES_PATH = '/debts/cost/guarantees';

export const DEBTS_BANK_LIST_PATH = '/debts/my-debts';

export const DEBTS_DEBENTURES_LIST_PATH = '/debts/debentures';

export const DEBTS_SECURITIZATIONS_LIST_PATH = '/debts/securitizations';

export const DEBTS_MUTUOS_LIST_PATH = '/debts/mutuos';

const itemLabelKey = (key: string): string => `debtsNav.item.${key}`;

const groupOf = (key: DebtsNavGroupKey, items: readonly DebtsNavItem[]): DebtsNavGroup => ({
  key, labelKey: `debtsNav.group.${key}`, items,
});

const SUMMARY: DebtsNavItem = { key: 'summary', path: DEBTS_OVERVIEW_PATH, labelKey: itemLabelKey('summary') };

const MATURITIES: DebtsNavItem = {
  key: 'maturities',
  path: DEBTS_MATURITIES_PATH,
  labelKey: itemLabelKey('maturities'),
  moduleTaskId: DEBT_MODULE_TASK_IDS.CLOSING,
};

const UPCOMING: DebtsNavItem = { key: 'upcoming', path: '/debts/upcoming', labelKey: itemLabelKey('upcoming') };

const COVENANTS: DebtsNavItem = {
  key: 'covenants',
  path: DEBTS_COVENANTS_PATH,
  labelKey: itemLabelKey('covenants'),
  moduleTaskId: DEBT_MODULE_TASK_IDS.COVENANTS,
};

const FAIR_VALUE: DebtsNavItem = {
  key: 'fair-value',
  path: DEBTS_FAIR_VALUE_PATH,
  labelKey: itemLabelKey('fair-value'),
  moduleTaskId: DEBT_MODULE_TASK_IDS.MARKET,
  isHidden: true,
};

const BANK: DebtsNavItem = { key: 'bank', path: DEBTS_BANK_LIST_PATH, labelKey: itemLabelKey('bank') };

const DEBENTURES: DebtsNavItem = { key: 'debentures', path: DEBTS_DEBENTURES_LIST_PATH, labelKey: itemLabelKey('debentures') };

const SECURITIZATIONS: DebtsNavItem = {
  key: 'securitizations', path: DEBTS_SECURITIZATIONS_LIST_PATH, labelKey: itemLabelKey('securitizations'),
};

const MUTUOS: DebtsNavItem = {
  key: 'mutuos', path: DEBTS_MUTUOS_LIST_PATH, labelKey: itemLabelKey('mutuos'), moduleTaskId: DEBT_MODULE_TASK_IDS.MUTUOS,
};

const HEDGES: DebtsNavItem = {
  key: 'hedges', path: '/debts/hedges', labelKey: itemLabelKey('hedges'), moduleTaskId: DEBT_MODULE_TASK_IDS.HEDGES,
};

const GUARANTEES: DebtsNavItem = {
  key: 'guarantees', path: '/debts/guarantees', labelKey: itemLabelKey('guarantees'), isPreview: true,
};

const CLOSINGS: DebtsNavItem = {
  key: 'closings', path: '/debts/closings', labelKey: itemLabelKey('closings'), moduleTaskId: DEBT_MODULE_TASK_IDS.CLOSING,
};

const MOVEMENTS: DebtsNavItem = {
  key: 'movements', path: DEBTS_MOVEMENTS_PATH, labelKey: itemLabelKey('movements'), moduleTaskId: DEBT_MODULE_TASK_IDS.CLOSING,
};

const COST_INDEXER: DebtsNavItem = {
  key: 'cost-indexer',
  path: DEBTS_COST_INDEXER_PATH,
  labelKey: itemLabelKey('cost-indexer'),
  moduleTaskId: DEBT_MODULE_TASK_IDS.CLOSING,
};

const COST_FUNDING: DebtsNavItem = {
  key: 'cost-funding',
  path: DEBTS_COST_FUNDING_PATH,
  labelKey: itemLabelKey('cost-funding'),
  moduleTaskId: DEBT_MODULE_TASK_IDS.CLOSING,
};

const COST_GUARANTEES: DebtsNavItem = {
  key: 'cost-guarantees', path: DEBTS_COST_GUARANTEES_PATH, labelKey: itemLabelKey('cost-guarantees'),
};

const COST_CHILDREN: readonly DebtsNavItem[] = [COST_INDEXER, COST_FUNDING, COST_GUARANTEES];

const COST: DebtsNavItem = {
  key: 'cost', path: COST_INDEXER.path, labelKey: itemLabelKey('cost'), children: COST_CHILDREN,
};

const COST_INDEXER_EXPOSURE: DebtsNavItem = { ...COST_INDEXER, labelKey: itemLabelKey('cost-indexer-exposure') };

const QUOTATIONS: DebtsNavItem = {
  key: 'quotations',
  path: '/debts/quotations',
  labelKey: itemLabelKey('quotations'),
  moduleTaskId: DEBT_MODULE_TASK_IDS.QUOTATIONS,
  isPreview: true,
};

const DRAFTS: DebtsNavItem = {
  key: 'drafts',
  path: '/debts/contract-drafts',
  labelKey: itemLabelKey('drafts'),
  moduleTaskId: DEBT_MODULE_TASK_IDS.CONTRACT_AI,
  isPreview: true,
};

const MARKET_INDEXES: DebtsNavItem = {
  key: 'market-indexes', path: DEBTS_MARKET_INDEXES_PATH, labelKey: itemLabelKey('market-indexes'),
};

const COMPANIES: DebtsNavItem = { key: 'companies', path: DEBTS_COMPANIES_PATH, labelKey: itemLabelKey('companies') };

const REPORTS: DebtsNavItem = {
  key: 'reports',
  path: '/debts/reports',
  labelKey: itemLabelKey('reports'),
  moduleTaskId: DEBT_MODULE_TASK_IDS.REPORTS,
};

const OPERATIONS_ITEMS: readonly DebtsNavItem[] = [BANK, DEBENTURES, SECURITIZATIONS, MUTUOS, HEDGES, GUARANTEES];

const QUOTATION_ITEMS: readonly DebtsNavItem[] = QUOTATIONS_ENABLED ? [QUOTATIONS] : [];

const REFERENCE_ITEMS: readonly DebtsNavItem[] = [MARKET_INDEXES, COMPANIES, REPORTS];

export const DEBTS_NAV_GROUPS_BY_BETA: Readonly<Record<DebtsNavBeta, readonly DebtsNavGroup[]>> = {
  tempo: [
    groupOf('portfolio', [SUMMARY, MATURITIES, UPCOMING, COVENANTS, FAIR_VALUE]),
    groupOf('operations', OPERATIONS_ITEMS),
    groupOf('closedMonth', [CLOSINGS, MOVEMENTS, COST]),
    groupOf('structuring', [...QUOTATION_ITEMS, DRAFTS]),
    groupOf('reference', REFERENCE_ITEMS),
  ],
  pergunta: [
    groupOf('owe', [SUMMARY, COST_INDEXER_EXPOSURE]),
    groupOf('costs', [COST_FUNDING, COST_GUARANTEES, FAIR_VALUE]),
    groupOf('maturing', [MATURITIES, UPCOMING, COVENANTS]),
    groupOf('closed', [CLOSINGS, MOVEMENTS]),
    groupOf('operations', [...OPERATIONS_ITEMS, ...QUOTATION_ITEMS, DRAFTS]),
    groupOf('reference', REFERENCE_ITEMS),
  ],
};

export const debtsNavGroups = (beta: DebtsNavBeta = DEFAULT_DEBTS_NAV_BETA): readonly DebtsNavGroup[] =>
  DEBTS_NAV_GROUPS_BY_BETA[beta];

export interface DebtsNavLocation {
  group: DebtsNavGroup;
  parent: DebtsNavItem | null;
  item: DebtsNavItem;
}

const locationsOf = (groups: readonly DebtsNavGroup[]): readonly DebtsNavLocation[] => groups.flatMap((group) =>
  group.items.flatMap((item) => [
    { group, parent: null, item },
    ...(item.children ?? []).map((child) => ({ group, parent: item, item: child })),
  ]));

const LOCATIONS_BY_BETA: Readonly<Record<DebtsNavBeta, readonly DebtsNavLocation[]>> = {
  tempo: locationsOf(DEBTS_NAV_GROUPS_BY_BETA.tempo),
  pergunta: locationsOf(DEBTS_NAV_GROUPS_BY_BETA.pergunta),
};

const SECTION_ROOT_PATHS: ReadonlySet<string> = new Set(
  LOCATIONS_BY_BETA.tempo.filter(({ item }) => !item.children).map(({ item }) => item.path),
);

export const debtsNavOrder = (location: DebtsNavLocation, beta: DebtsNavBeta = DEFAULT_DEBTS_NAV_BETA): number =>
  LOCATIONS_BY_BETA[beta].findIndex(({ item }) => item.key === location.item.key);

export const debtsSubpath = (pathname: string): string => {
  const start = pathname.indexOf(DEBTS_OVERVIEW_PATH);
  const subpath = start === -1 ? pathname : pathname.slice(start).split(/[?#]/)[0];
  return subpath.length > 1 && subpath.endsWith('/') ? subpath.slice(0, -1) : subpath;
};

const matchLength = (subpath: string, prefix: string): number =>
  (subpath === prefix || subpath.startsWith(`${prefix}/`) ? prefix.length : -1);

const locationScore = (subpath: string, { item }: DebtsNavLocation): number => Math.max(
  matchLength(subpath, item.path),
  ...(item.aliases ?? []).map((alias) => matchLength(subpath, alias)),
);

export const locateDebtsNav = (pathname: string, beta: DebtsNavBeta = DEFAULT_DEBTS_NAV_BETA): DebtsNavLocation => {
  const subpath = debtsSubpath(pathname);
  const locations = LOCATIONS_BY_BETA[beta];
  const fallback = locations[0];
  return locations.reduce<{ location: DebtsNavLocation; score: number }>((best, location) => {
    const score = locationScore(subpath, location);
    return score >= best.score ? { location, score } : best;
  }, { location: fallback, score: 0 }).location;
};

export const isDebtsSectionRoot = (pathname: string): boolean => SECTION_ROOT_PATHS.has(debtsSubpath(pathname));

const FOCUS_MODE_PATTERN = /\/(new|edit)$/;

export const isDebtsFocusMode = (pathname: string): boolean => FOCUS_MODE_PATTERN.test(debtsSubpath(pathname));

export type DebtsSectionAccess = (item: Pick<DebtsNavItem, 'moduleTaskId'>) => DebtModuleAccess;

const isHiddenItem = (item: DebtsNavItem, accessOf: DebtsSectionAccess): boolean => {
  if (item.isPendingRoute || item.isHidden) return true;
  if (item.children) return visibleNavItems(item.children, accessOf).length === 0;
  return accessOf(item) === 'hidden';
};

export const visibleNavItems = (items: readonly DebtsNavItem[], accessOf: DebtsSectionAccess): DebtsNavItem[] =>
  items.filter((item) => !isHiddenItem(item, accessOf));

export const firstVisibleChild = (item: DebtsNavItem, accessOf: DebtsSectionAccess): DebtsNavItem | null =>
  visibleNavItems(item.children ?? [], accessOf)[0] ?? null;

const strongestAccess = (accesses: readonly DebtModuleAccess[]): DebtModuleAccess => {
  if (accesses.includes('enabled')) return 'enabled';
  if (accesses.includes('locked')) return 'locked';
  return 'hidden';
};

const itemAccess = (item: DebtsNavItem, accessOf: DebtsSectionAccess): DebtModuleAccess =>
  (item.children ? strongestAccess(item.children.map(accessOf)) : accessOf(item));

export const groupAccess = (group: DebtsNavGroup, accessOf: DebtsSectionAccess): DebtModuleAccess =>
  strongestAccess(group.items.filter((item) => !item.isPendingRoute && !item.isHidden).map((item) => itemAccess(item, accessOf)));

const FILTER_PARAMS = ['carteira', 'banco'] as const;

const MONTH_PARAMS = ['mapaData', 'mapaComparar'] as const;

const SCOPE_SHARING_KEYS: ReadonlySet<string> = new Set([
  'summary', 'maturities', 'movements', 'cost', 'cost-indexer', 'cost-funding', 'cost-guarantees', 'covenants', 'upcoming',
]);

const MONTH_SHARING_KEYS: ReadonlySet<string> = new Set(['movements', 'cost', 'cost-indexer', 'cost-funding']);

export const navTargetParams = (
  from: DebtsNavItem,
  to: DebtsNavItem,
  source: URLSearchParams,
): Record<string, string | string[]> => {
  const carried: Record<string, string | string[]> = {};
  const copy = (key: string) => {
    const value = source.get(key);
    if (value) carried[key] = value;
  };
  const beta = navBetaFromUrl(source);
  if (beta) carried[NAV_BETA_PARAM] = beta;
  if (SCOPE_SHARING_KEYS.has(from.key) && SCOPE_SHARING_KEYS.has(to.key)) {
    FILTER_PARAMS.forEach(copy);
    const scope = readScopeParam(source);
    if (scope.length > 0) carried[SCOPE_PARAM] = scope;
  }
  if (MONTH_SHARING_KEYS.has(from.key) && MONTH_SHARING_KEYS.has(to.key)) MONTH_PARAMS.forEach(copy);
  return carried;
};
